import { Controller } from '@hotwired/stimulus';
import { dropdownMaxHeightPx } from '../../utils/dropdown_max_height';

export default class extends Controller {
  static targets = ['wrapper', 'button', 'buttonLabel', 'hidden', 'option', 'helper'];
  static values = {
    direction: { type: String, default: 'auto' }, // 'auto' | 'bottom' | 'top'
    minWidth: Number, // px
    maxWidth: Number, // px
  };

  isDropdownOpen = false;
  outsideBound = false;

  connect() {
    const dd = this.element.querySelector('[data-ss-dropdown]');
    if (!dd) return;
    this.dropdownEl = dd;
    this.dropdownOriginParent = dd.parentElement;

    this.portalRoot = this._ensurePortalRoot();

    this._syncFromDOM();
    this._initSelectedColor();

    const hasError = this.hiddenTarget.dataset.hasError === 'true';
    if (hasError) this.showError();

    this.addValidationListener();
  }

  disconnect() {
    this._teardownOutside();
    this._detachObservers();
    this._unbindDropdownHandlers();
    if (this._validatedForm && this._onFormSubmit) {
      this._validatedForm.removeEventListener('submit', this._onFormSubmit);
    }
    if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
      this.dropdownOriginParent.appendChild(this.dropdownEl);
    }
  }

  addValidationListener() {
    const form = this.element.closest('form');
    if (!form) return;

    this._onFormSubmit = (event) => {
      if (this.hasHiddenTarget && this.hiddenTarget.disabled) return;

      const validatesOnSubmit = this.buttonTarget.dataset.frontendValidation === 'true';
      const value = this.hasHiddenTarget ? this.hiddenTarget.value : '';

      if (validatesOnSubmit && (!value || value.trim() === '')) {
        event.preventDefault();
        event.stopPropagation();
        this.showValidationError();
        this.buttonTarget.focus();
      }
    };

    form.addEventListener('submit', this._onFormSubmit);
    this._validatedForm = form;
  }

  showValidationError() {
    this.showError();

    if (!this.hasHelperTarget) {
      const helperText = document.createElement('p');
      helperText.dataset.singleSelectFieldTarget = 'helper';
      helperText.className = 'mt-4 text-paragraph-s text-red-1000';
      const label = this.element.querySelector('label');
      const fieldName = label ? label.textContent.trim().replace(/\s*\*\s*$/, '') : 'Field';
      helperText.textContent = `${fieldName} can't be blank`;
      this.element.appendChild(helperText);
    } else {
      this.helperTarget.style.display = '';
      this.helperTarget.classList.add('text-red-1000');
    }
  }

  clearValidationError() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.remove('border-red-300', 'has-error');
    }

    this.buttonTarget.classList.remove(
      'text-red-1000',
      'placeholder-red-1000',
      'border-red-300',
      'border-l',
      'border-l-red-300',
    );

    if (this.hasHelperTarget && this.helperTarget.dataset.backendError !== 'true') {
      this.helperTarget.style.display = 'none';
    }
  }

  // === API ===
  toggle(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.isDropdownOpen ? this.close() : this.open();
  }

  open() {
    if (this.isDropdownOpen) return;

    this.portalRoot.appendChild(this.dropdownEl);

    this.dropdownEl.classList.remove('hidden');
    this.dropdownEl.classList.add('invisible', 'ss-transition-none', 'opacity-0', 'scale-95');

    // A11y
    this.buttonTarget.setAttribute('aria-expanded', 'true');

    this._positionDropdown();
    this._bindDropdownHandlers();

    void this.dropdownEl.offsetHeight;

    this.dropdownEl.classList.remove('ss-transition-none', 'invisible');

    requestAnimationFrame(() => {
      this.dropdownEl.classList.remove('opacity-0', 'scale-95');
      this.dropdownEl.classList.add('opacity-100', 'scale-100');
    });

    this._setupOutside();
    this._attachObservers();
    this.isDropdownOpen = true;
  }

  close() {
    if (!this.isDropdownOpen) return;

    this.dropdownEl.classList.add('opacity-0', 'scale-95');
    this.dropdownEl.classList.remove('opacity-100', 'scale-100');

    const onEnd = () => {
      this.dropdownEl.classList.add('hidden');
      this.dropdownEl.removeEventListener('transitionend', onEnd);
    };
    this.dropdownEl.addEventListener('transitionend', onEnd);

    // A11y
    this.buttonTarget.setAttribute('aria-expanded', 'false');

    this._unbindDropdownHandlers();
    this._teardownOutside();
    this._detachObservers();
    this.isDropdownOpen = false;
  }

  // === Selection ===
  selectFromItem(event) {
    const li =
      event?.currentTarget?.closest?.('li[data-value]') ||
      event?.target?.closest?.('li[data-value]');
    if (!li) return;
    if (li.dataset.disabled === 'true') return;
    const input = li.querySelector('input[type="radio"]');
    if (input && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const value = (li.dataset.value || input?.value || '').trim();
      const label = (input?.dataset.label || li.querySelector('span')?.textContent || value).trim();
      this._applySelection(label, value);
    }
  }

  setValue(value) {
    // If targets aren't available, try to find them manually
    let options = this.optionTargets;
    if (!options || options.length === 0) {
      // Look for options in both the original location and the portal
      const dropdown = this.dropdownEl || this.element.querySelector('[data-ss-dropdown]');
      if (dropdown) {
        options = Array.from(dropdown.querySelectorAll('[data-single-select-field-target="option"]'));
      }
    }

    const option = options.find((el) => el.dataset.value === value)
    if (!option) return;

    const input = option.querySelector('input[type="radio"]');
    if (input) {
      input.checked = true;
      const label = input.dataset.label || value;
      this._updateValue(label, value);
    }
  }

  clearSelection() {
    const placeholder = this.buttonTarget.dataset.placeholder || '';
    this._updateValue(placeholder, '');
    if (this.dropdownEl) {
      this.dropdownEl.querySelectorAll('input[type="radio"]').forEach((input) => { input.checked = false; });
    }
    this.close();
  }

  _initSelectedColor() {
    const value = this.hasHiddenTarget ? this.hiddenTarget.value : '';
    const color = value ? this._colorForValue(value) : null;
    this._appliedColorClasses = color ? color.split(/\s+/).filter(Boolean) : null;
  }

  _colorForValue(value) {
    const dropdown = this.dropdownEl || this.element.querySelector('[data-ss-dropdown]');
    const option = dropdown?.querySelector(`[data-single-select-field-target="option"][data-value="${CSS.escape(value)}"]`);
    return option?.dataset.color || null;
  }

  _applySelectedColor(rawValue) {
    if (!this.hasButtonLabelTarget) return;

    if (this._appliedColorClasses) {
      this.buttonLabelTarget.classList.remove(...this._appliedColorClasses);
      this._appliedColorClasses = null;
    }

    const color = rawValue ? this._colorForValue(rawValue) : null;
    if (!color) return;

    this._appliedColorClasses = color.split(/\s+/).filter(Boolean);
    this.buttonLabelTarget.classList.add(...this._appliedColorClasses);
  }

  _updateValue(label, rawValue) {
    if (this.hasButtonLabelTarget) this.buttonLabelTarget.textContent = label || '';
    else this.buttonTarget.innerText = label || '';

    this._applySelectedColor(rawValue);

    if (!rawValue || label === (this.buttonTarget.dataset.placeholder || '')) {
      this.buttonTarget.classList.remove('text-black-1000');
      this.buttonTarget.classList.add('text-black-400');
    } else {
      this.buttonTarget.classList.remove('text-black-400');
      this.buttonTarget.classList.add('text-black-1000');
    }

    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = rawValue;
      this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  _syncFromDOM() {
    const currentValue = this.hasButtonLabelTarget
      ? (this.buttonLabelTarget?.textContent || '').trim()
      : this.buttonTarget.innerText.trim();

    const placeholder = this.buttonTarget.dataset.placeholder || '';

    if (currentValue === placeholder) {
      this.buttonTarget.classList.add('text-black-400');
      this.buttonTarget.classList.remove('text-black-1000');
    } else {
      this.buttonTarget.classList.add('text-black-1000');
      this.buttonTarget.classList.remove('text-black-400');
    }
  }

  _ensurePortalRoot() {
    let root = document.getElementById('ui-layer-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'ui-layer-root';
      root.classList.add('ui-layer-root');
      document.body.appendChild(root);
    }
    return root;
  }

  _positionDropdown() {
    const btnRect = this.buttonTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const M = 8;

    const minW = this.hasMinWidthValue ? this.minWidthValue : 200;
    const maxW = this.hasMaxWidthValue ? this.maxWidthValue : Infinity;
    const width = Math.max(minW, Math.min(btnRect.width, maxW));

    this.dropdownEl.classList.add('ss-no-max-h');
    const naturalH = this.dropdownEl.offsetHeight || 300;
    this.dropdownEl.classList.remove('ss-no-max-h');

    const spaceBelow = vh - btnRect.bottom - M;
    const spaceAbove = btnRect.top - M;
    const wantsTop = this.directionValue === 'top';
    const wantsBottom = this.directionValue === 'bottom';

    let openUp = wantsTop
      ? true
      : wantsBottom
        ? false
        : spaceBelow < Math.min(naturalH, 320) && spaceAbove > spaceBelow;

    let top = openUp ? Math.max(M, btnRect.top - Math.min(naturalH, spaceAbove)) : btnRect.bottom;
    const maxH = openUp ? spaceAbove : spaceBelow;

    let left = btnRect.left;
    if (left + width + M > vw) left = Math.max(M, vw - width - M);
    if (left < M) left = M;

    this._setVar('--ss-left', `${Math.round(left)}px`);
    this._setVar('--ss-top', `${Math.round(top)}px`);
    this._setVar('--ss-width', `${Math.round(width)}px`);
    this._setVar('--ss-max-h', `${dropdownMaxHeightPx(naturalH, maxH)}px`);
  }

  _setVar(name, value) {
    this.dropdownEl.style.setProperty(name, value);
  }

  _attachObservers() {
    this._reposition = this._reposition.bind(this);
    this._onScroll = (e) => {
      if (this.dropdownEl && this.dropdownEl.contains(e.target)) return;
      this._reposition();
    };
    window.addEventListener('scroll', this._onScroll, true);
    window.addEventListener('resize', this._reposition);
    this.ro = new ResizeObserver(this._reposition);
    this.ro.observe(this.buttonTarget);

    if (window.visualViewport) {
      this.vvo = { onResize: this._reposition, onScroll: this._reposition };
      window.visualViewport.addEventListener('resize', this.vvo.onResize);
      window.visualViewport.addEventListener('scroll', this.vvo.onScroll);
    }
  }

  _detachObservers() {
    window.removeEventListener('scroll', this._onScroll, true);
    window.removeEventListener('resize', this._reposition);
    this.ro?.disconnect();
    this.ro = undefined;
    if (this.vvo) {
      window.visualViewport.removeEventListener('resize', this.vvo.onResize);
      window.visualViewport.removeEventListener('scroll', this.vvo.onScroll);
      this.vvo = undefined;
    }
  }

  _reposition = () => {
    if (this.isDropdownOpen) this._positionDropdown();
  };

  _setupOutside() {
    if (this.outsideBound) return;
    this._onDocPointerDown = (e) => {
      const t = e.target;
      const clickedInsideDropdown = this.dropdownEl.contains(t);
      const clickedInsideComponent = this.element.contains(t);
      if (!clickedInsideDropdown && !clickedInsideComponent) this.close();
    };
    this._onDocKeydown = (e) => {
      if (e.key === 'Escape') this.close();
    };

    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onDocKeydown);
    this.outsideBound = true;
  }

  _teardownOutside() {
    if (!this.outsideBound) return;
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onDocKeydown);
    this.outsideBound = false;
  }

  _applySelection(label, value) {
    this._updateValue(label, value);
    this.clearValidationError();
    this.close();
  }

  _bindDropdownHandlers() {
    if (this._dropdownHandlersBound || !this.dropdownEl) return;

    this._onDropdownChange = (e) => {
      const input = e.target?.closest?.('input[type="radio"]');
      if (input && this.dropdownEl.contains(input)) {
        if (input.disabled || input.closest('li[data-value]')?.dataset.disabled === 'true') return;
        const value = (input.value || '').trim();
        const label = (input.dataset.label || input.closest('label')?.textContent || value).trim();
        this._applySelection(label, value);
      }
    };

    this._onDropdownClick = (e) => {
      const li = e.target?.closest?.('li[data-value]');
      if (li && this.dropdownEl.contains(li)) {
        if (li.dataset.disabled === 'true') {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const input = li.querySelector('input[type="radio"]');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const value = (li.dataset.value || input?.value || '').trim();
          const labelNode = li.querySelector('[data-label]') || li.querySelector('span');
          const label = (input?.dataset.label || labelNode?.textContent || value).trim();
          this._applySelection(label, value);
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const clearBtn = e.target?.closest?.('[data-action*="clearSelection"]');
      if (clearBtn && this.dropdownEl.contains(clearBtn)) {
        e.preventDefault();
        e.stopPropagation();
        this.clearSelection();
      }
    };

    this.dropdownEl.addEventListener('change', this._onDropdownChange);
    this.dropdownEl.addEventListener('click', this._onDropdownClick);
    this._dropdownHandlersBound = true;
  }

  _unbindDropdownHandlers() {
    if (!this._dropdownHandlersBound || !this.dropdownEl) return;
    this.dropdownEl.removeEventListener('change', this._onDropdownChange);
    this.dropdownEl.removeEventListener('click', this._onDropdownClick);
    this._dropdownHandlersBound = false;
    this._onDropdownChange = null;
    this._onDropdownClick = null;
  }

  showError() {
    if (this.wrapperTarget) {
      this.wrapperTarget.classList.add('border-red-300');
      this.wrapperTarget.classList.add('has-error');
    }

    this.buttonTarget.classList.add(
      'text-red-1000',
      'placeholder-red-1000',
      'border-red-300',
      'border-l',
      'border-l-red-300',
    );
  }
}
